import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useEffect } from "react";
import { useApi } from "@/core/api";
import {
  aiScriptSpreadsheetSheetKey,
  aiScriptSpreadsheetSheetQueryOptions,
  cancelChangedSpreadsheetSheetWarmup,
  getAiScriptSpreadsheetSheetPath,
} from "@/payroll/ai-script/spreadsheet-sheet-query";
import type {
  IAiSpreadsheetResponse,
  IAiSpreadsheetSheetResponse,
  CachedSpreadsheetSheetResponse,
} from "@/payroll/ai-script/types";
import { cancelWorkbookCellEdits } from "@/payroll/ai-script/workbook-cell-edit-batcher";
import { cancelQueuedWorkbookMutations } from "@/payroll/ai-script/workbook-mutation";
import {
  WorkbookOperationFailedError,
  completeWorkbookOperation,
  recoverWorkbookOperation,
  setWorkbookOperationRecoveryError,
  useWorkbookOperationSnapshot,
} from "@/payroll/ai-script/workbook-operation";
import type { WorkbookReadSource } from "@/payroll/ai-script/workbook-read-source";
import { aiScriptSpreadsheetKey } from "./keys";
import { getAiScriptSpreadsheetPath } from "./paths";
import { refreshWorkbookAfterOperation } from "./workbook-refresh";

export const aiScriptSpreadsheetQueryOptions = (
  api: AxiosInstance,
  businessId: string | null,
  runId: string | null,
) => ({
  queryKey: aiScriptSpreadsheetKey(businessId, runId),
  queryFn: ({ signal }: { signal: AbortSignal }) => {
    if (!businessId || !runId) throw new Error("Payroll run is required");

    return api
      .get(getAiScriptSpreadsheetPath(businessId, runId), { signal })
      .then((res) => res.data as IAiSpreadsheetResponse);
  },
  retry: false,
});

export const useRecoverAiScriptWorkbookOperation = (
  businessId: string | null,
  runId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const snapshot = useWorkbookOperationSnapshot(businessId, runId);

  useEffect(() => {
    if (!businessId || !runId) return;
    let active = true;
    const controller = new AbortController();

    const recover = async () => {
      try {
        const result = await recoverWorkbookOperation(
          api,
          businessId,
          runId,
          controller.signal,
        );
        if (!active) return;
        if (!result) {
          completeWorkbookOperation(businessId, runId);
          return;
        }
        await refreshWorkbookAfterOperation(queryClient, businessId, runId);
        if (active) completeWorkbookOperation(businessId, runId);
      } catch (error) {
        if (!active || error instanceof WorkbookOperationFailedError) return;

        const errorMessage =
          error instanceof Error
            ? error.message
            : "Grid recalculation status could not be refreshed.";
        setWorkbookOperationRecoveryError(
          businessId,
          runId,
          errorMessage,
          () => {
            void recover();
          },
        );
      }
    };

    void recover();
    return () => {
      active = false;
      controller.abort();
    };
  }, [api, businessId, queryClient, runId]);

  useEffect(
    () => () => {
      if (businessId && runId) {
        cancelQueuedWorkbookMutations(businessId, runId);
        cancelWorkbookCellEdits(businessId, runId);
        cancelChangedSpreadsheetSheetWarmup(businessId, runId);
      }
    },
    [businessId, runId],
  );

  return snapshot;
};

export const useGetAiScriptSpreadsheet = (
  businessId: string | null,
  runId: string | null,
  source?: WorkbookReadSource,
) => {
  const api = useApi();
  return useQuery<IAiSpreadsheetResponse>({
    ...(source
      ? {
          queryKey: [source.cacheKey, businessId, runId, "workbook"],
          queryFn: ({ signal }: { signal: AbortSignal }) =>
            api
              .get(source.workbookPath, { signal })
              .then((res) => res.data as IAiSpreadsheetResponse),
          retry: false,
        }
      : aiScriptSpreadsheetQueryOptions(api, businessId, runId)),
    enabled: !!businessId && !!runId,
  });
};

export const useLoadAiScriptSpreadsheetSheet = (
  businessId: string | null,
  runId: string | null,
  sheetId: string | null,
  shouldLoad: boolean,
  source?: WorkbookReadSource,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useQuery<CachedSpreadsheetSheetResponse>({
    ...(source
      ? {
          queryKey: aiScriptSpreadsheetSheetKey(
            businessId,
            runId,
            sheetId,
            source,
          ),
          queryFn: ({ signal }: { signal: AbortSignal }) => {
            if (!businessId || !runId || !sheetId) {
              throw new Error("Workbook sheet is required");
            }

            return api
              .get(
                getAiScriptSpreadsheetSheetPath(
                  businessId,
                  runId,
                  sheetId,
                  source,
                ),
                { signal },
              )
              .then((res) => res.data as IAiSpreadsheetSheetResponse);
          },
          retry: false,
          staleTime: Infinity,
        }
      : aiScriptSpreadsheetSheetQueryOptions(
          api,
          businessId,
          runId,
          sheetId,
          undefined,
          queryClient,
        )),
    enabled: !!businessId && !!runId && !!sheetId && shouldLoad,
  });
};
